// Draft + chat storage (OPFS-SQLite). Local-first: editing a draft
// writes here instantly and survives reloads (Sprint 3 "Done when:
// draft persists across reloads"). A debounced backup mirrors drafts to
// the private repo (lib/drafts-backup.ts) — SQLite is the working
// cache, the private repo is canonical, matching the 4G git-canonical
// pattern.
//
// Chat messages are keyed by draft id so a conversation spans its
// draft: switch drafts, see that draft's thread.

import { exec, query } from "./sqlite";

export interface Draft {
  id: string;
  google_sub: string;
  title: string;
  body: string;
  tags: string;          // JSON array
  summary: string;
  /** Set once the draft has been published → this is its live slug.
   *  A "published post" is exactly a draft with a non-null slug. */
  published_slug: string | null;
  /** Epoch ms of first publish (stable across edits, for feed ordering). */
  published_at: number | null;
  created_at: number;
  updated_at: number;
}

export interface ChatMessage {
  id: string;
  draft_id: string;
  role: "user" | "assistant";
  content: string;
  created_at: number;
}

let initialized = false;

async function ensureTables(): Promise<void> {
  if (initialized) return;
  await exec(`
    CREATE TABLE IF NOT EXISTS drafts (
      id              TEXT PRIMARY KEY,
      google_sub      TEXT NOT NULL,
      title           TEXT NOT NULL DEFAULT '',
      body            TEXT NOT NULL DEFAULT '',
      tags            TEXT NOT NULL DEFAULT '[]',
      summary         TEXT NOT NULL DEFAULT '',
      published_slug  TEXT,
      created_at      INTEGER NOT NULL,
      updated_at      INTEGER NOT NULL
    )
  `);
  await exec(`
    CREATE TABLE IF NOT EXISTS chat_messages (
      id          TEXT PRIMARY KEY,
      draft_id    TEXT NOT NULL,
      role        TEXT NOT NULL,
      content     TEXT NOT NULL,
      created_at  INTEGER NOT NULL
    )
  `);
  await exec(`CREATE INDEX IF NOT EXISTS idx_chat_draft ON chat_messages(draft_id, created_at)`);
  // Migration: add published_at to DBs created before Sprint 5. ALTER
  // throws "duplicate column" once it exists — swallow that.
  try { await exec(`ALTER TABLE drafts ADD COLUMN published_at INTEGER`); } catch { /* already present */ }
  initialized = true;
}

// A small id without Math.random's full surface — timestamp + counter
// is plenty unique for a single browser's local drafts.
let idCounter = 0;
function makeId(prefix: string): string {
  idCounter = (idCounter + 1) % 100000;
  return `${prefix}_${Date.now().toString(36)}_${idCounter.toString(36)}`;
}

export async function listDrafts(googleSub: string): Promise<Draft[]> {
  await ensureTables();
  return query<Draft>(
    `SELECT * FROM drafts WHERE google_sub = ? ORDER BY updated_at DESC`,
    [googleSub],
  );
}

export async function getDraft(id: string): Promise<Draft | null> {
  await ensureTables();
  const rows = await query<Draft>(`SELECT * FROM drafts WHERE id = ?`, [id]);
  return rows[0] ?? null;
}

export async function createDraft(googleSub: string, seed?: Partial<Draft>): Promise<Draft> {
  await ensureTables();
  const now = Date.now();
  const id = seed?.id ?? makeId("draft");
  await exec(
    `INSERT INTO drafts (id, google_sub, title, body, tags, summary, published_slug, published_at, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      id, googleSub,
      seed?.title ?? "", seed?.body ?? "",
      seed?.tags ?? "[]", seed?.summary ?? "",
      seed?.published_slug ?? null, seed?.published_at ?? null, now, now,
    ],
  );
  return (await getDraft(id))!;
}

/** All published posts for a creator (drafts with a slug), newest first. */
export async function listPublished(googleSub: string): Promise<Draft[]> {
  await ensureTables();
  return query<Draft>(
    `SELECT * FROM drafts WHERE google_sub = ? AND published_slug IS NOT NULL ORDER BY published_at DESC`,
    [googleSub],
  );
}

/** Mark a draft published (or update its publish metadata). Idempotent:
 *  re-publishing keeps the original published_at. */
export async function setPublished(id: string, slug: string, publishedAt: number): Promise<void> {
  await ensureTables();
  await exec(
    `UPDATE drafts SET published_slug = ?, published_at = COALESCE(published_at, ?), updated_at = ? WHERE id = ?`,
    [slug, publishedAt, Date.now(), id],
  );
}

/** Pull a post back to draft-only state. */
export async function clearPublished(id: string): Promise<void> {
  await ensureTables();
  await exec(`UPDATE drafts SET published_slug = NULL, published_at = NULL, updated_at = ? WHERE id = ?`, [Date.now(), id]);
}

/** Is this slug already taken by a different published draft? */
export async function slugTaken(googleSub: string, slug: string, exceptId: string): Promise<boolean> {
  await ensureTables();
  const rows = await query<{ n: number }>(
    `SELECT COUNT(*) AS n FROM drafts WHERE google_sub = ? AND published_slug = ? AND id != ?`,
    [googleSub, slug, exceptId],
  );
  return (rows[0]?.n ?? 0) > 0;
}

export async function updateDraft(
  id: string,
  patch: Partial<Pick<Draft, "title" | "body" | "tags" | "summary" | "published_slug">>,
): Promise<void> {
  await ensureTables();
  const sets: string[] = [];
  const binds: unknown[] = [];
  for (const k of ["title", "body", "tags", "summary", "published_slug"] as const) {
    if (k in patch) { sets.push(`${k} = ?`); binds.push((patch as Record<string, unknown>)[k] ?? null); }
  }
  if (sets.length === 0) return;
  sets.push(`updated_at = ?`);
  binds.push(Date.now(), id);
  await exec(`UPDATE drafts SET ${sets.join(", ")} WHERE id = ?`, binds);
}

export async function deleteDraft(id: string): Promise<void> {
  await ensureTables();
  await exec(`DELETE FROM drafts WHERE id = ?`, [id]);
  await exec(`DELETE FROM chat_messages WHERE draft_id = ?`, [id]);
}

export async function listMessages(draftId: string): Promise<ChatMessage[]> {
  await ensureTables();
  return query<ChatMessage>(
    `SELECT * FROM chat_messages WHERE draft_id = ? ORDER BY created_at ASC`,
    [draftId],
  );
}

export async function addMessage(draftId: string, role: "user" | "assistant", content: string): Promise<ChatMessage> {
  await ensureTables();
  const msg: ChatMessage = { id: makeId("msg"), draft_id: draftId, role, content, created_at: Date.now() };
  await exec(
    `INSERT INTO chat_messages (id, draft_id, role, content, created_at) VALUES (?, ?, ?, ?, ?)`,
    [msg.id, msg.draft_id, msg.role, msg.content, msg.created_at],
  );
  return msg;
}
